import { encodeCode128, encodeEan13 } from "./barcode.js";
import { PRINT_FONT_SCALE, } from "./document.js";
import { BLANK_PRINT_TITLE } from "./layout-html.js";
import { layout, pairParts } from "./text.js";
const escapeHtml = (value) => value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
/** Milímetros com no máximo duas casas, sem `1.2000000000000002mm`. */
const mm = (value) => `${String(Math.round(value * 100) / 100)}mm`;
/**
 * Uma página é UMA FILEIRA de adesivos.
 *
 * Numa bobina (`columns: 1`) isso é exatamente um adesivo por página, que é o
 * que o driver da térmica espera. Numa folha de 3 colunas, o navegador empilha
 * as fileiras e pagina sozinho. A alternativa — declarar a folha inteira e
 * contar quantas fileiras cabem — precisaria saber a altura do papel, que é
 * justamente o que o driver sabe e nós não.
 */
const pageWidthMm = (sheet) => sheet.columns * sheet.widthMm + (sheet.columns - 1) * sheet.gapMm;
/**
 * O código de barras como SVG, em unidades de módulo.
 *
 * O `viewBox` conta módulos e o elemento mede milímetros; quem converte um no
 * outro é o navegador, com a aritmética exata que ele já tem. Desenhar as
 * barras em `mm` calculado aqui acumularia arredondamento barra a barra — e
 * meio décimo de milímetro somado 95 vezes é um símbolo que não lê.
 */
const barcodeSvg = (pattern, heightMm) => {
    const rects = [];
    let x = 0;
    pattern.modules.forEach((width, index) => {
        // Índice par é barra; ímpar é espaço, e espaço não se desenha.
        if (index % 2 === 0) {
            rects.push(`<rect x="${String(x)}" y="0" width="${String(width)}" height="100"/>`);
        }
        x += width;
    });
    return [
        `<svg class="bars" viewBox="0 0 ${String(pattern.totalModules)} 100"`,
        ` preserveAspectRatio="none" style="height:${mm(heightMm)}"`,
        ` shape-rendering="crispEdges" role="img"`,
        ` aria-label="Código de barras ${escapeHtml(pattern.text)}">`,
        rects.join(''),
        '</svg>',
    ].join('');
};
const renderBlock = (block, sheet) => {
    switch (block.kind) {
        case 'text': {
            const classes = ['line'];
            if (block.big === true)
                classes.push('big');
            if (block.bold === true)
                classes.push('bold');
            return `<div class="${classes.join(' ')}">${escapeHtml(block.text)}</div>`;
        }
        case 'barcode': {
            const pattern = block.symbology === 'ean13'
                ? encodeEan13(block.data)
                : encodeCode128(block.data);
            /*
             * A altura das barras é proporcional ao adesivo, não fixa: a mesma
             * etiqueta sai em 30 mm e em 15 mm de altura, e um código de barras de
             * 12 mm num adesivo de 15 mm não deixa espaço para o preço. 30% é o que
             * mantém as barras altas o bastante para o leitor e o resto legível.
             */
            const barsMm = Math.max(6, (sheet.heightMm - sheet.marginMm * 2) * 0.3);
            return [
                '<div class="barcode">',
                barcodeSvg(pattern, barsMm),
                `<div class="hri">${escapeHtml(pattern.text)}</div>`,
                '</div>',
            ].join('');
        }
        case 'pair':
            return `<div class="line"><span>${escapeHtml(block.label)}</span><span>${escapeHtml(block.value)}</span></div>`;
        case 'rule':
            return '<hr/>';
        // Avanço de papel e corte são fatos sobre bobina. Numa folha de adesivo não
        // existem, e ignorá-los é a tradução certa — não um caso esquecido.
        case 'feed':
        case 'cut':
            return '';
        // Tabela é coisa de folha A4 (F170); no adesivo não cabe e não existe.
        case 'table':
            return '';
    }
};
const styles = (sheet) => `
  @page { size: ${mm(pageWidthMm(sheet))} ${mm(sheet.heightMm)}; margin: 0 }
  * { box-sizing: border-box }
  html, body { margin: 0; padding: 0 }
  body {
    font-family: Helvetica, Arial, sans-serif;
    /* Preto puro: a térmica é de um canal só, e cinza vira chuvisco. */
    color: #000;
    background: #fff;
  }
  .row {
    display: flex;
    gap: ${mm(sheet.gapMm)};
    width: ${mm(pageWidthMm(sheet))};
    height: ${mm(sheet.heightMm)};
    /* A quebra é do navegador, mas nunca no meio de uma fileira. */
    break-inside: avoid;
    page-break-inside: avoid;
  }
  .label {
    width: ${mm(sheet.widthMm)};
    height: ${mm(sheet.heightMm)};
    padding: ${mm(sheet.marginMm)};
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: ${mm(Math.min(1, sheet.marginMm))};
    overflow: hidden;
  }
  .line {
    text-align: center;
    line-height: 1.15;
    font-size: ${mm(sheet.heightMm * 0.1)};
    /* Duas linhas e para. Um nome longo empurraria o preço para fora do
       adesivo, e o que se perde no papel não avisa que se perdeu. */
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  .bold { font-weight: 700 }
  .big { font-size: ${mm(sheet.heightMm * 0.22)}; letter-spacing: -0.02em }
  .barcode { width: 100%; display: flex; flex-direction: column; align-items: center }
  .bars { width: 100%; display: block; fill: #000 }
  .hri {
    font-family: "Courier New", monospace;
    font-size: ${mm(sheet.heightMm * 0.075)};
    letter-spacing: 0.08em;
  }
`;
const document_ = (title, css, body) => [
    '<!doctype html>',
    '<html lang="pt-BR"><head><meta charset="utf-8"/>',
    `<title>${escapeHtml(title)}</title>`,
    `<style>${css}</style>`,
    `</head><body>${body}</body></html>`,
].join('');
/**
 * A folha inteira, pronta para o `iframe`.
 *
 * Documento autocontido: nenhum `<link>`, nenhuma fonte externa, nenhum
 * `app.css`. Um `@font-face` que demore para carregar imprime a página com a
 * fonte errada e ninguém vê a tempo.
 */
export const renderLabelSheetHtml = (documents, sheet) => {
    const rows = [];
    for (let index = 0; index < documents.length; index += sheet.columns) {
        const cells = documents
            .slice(index, index + sheet.columns)
            .map((doc) => `<div class="label">${doc.blocks.map((block) => renderBlock(block, sheet)).join('')}</div>`);
        rows.push(`<div class="row">${cells.join('')}</div>`);
    }
    return document_(BLANK_PRINT_TITLE, styles(sheet), rows.join(''));
};
/**
 * A etiqueta de teste, com uma régua de 10 mm desenhada.
 *
 * É a primeira coisa deste módulo que o dono consegue usar, e existe por um
 * motivo específico: `@page { size }` é uma DICA. Quem decide o papel é o
 * driver da impressora no Windows, e o diálogo do Chrome abre em "Margens:
 * Padrão" e "Escala: Padrão" — os dois deslocam as barras.
 *
 * Sem esta régua, o primeiro relato é "saiu torto" e há quatro lugares
 * possíveis para o erro. Com ela há um: se os 10 mm impressos medirem 10 mm na
 * régua de verdade, o par CSS↔driver está calibrado e o problema é outro.
 */
export const renderCalibrationHtml = (sheet) => {
    const ruler = [
        '<div class="ruler">',
        '<div class="ruler-bar"></div>',
        '<div class="ruler-text">10 mm — meça com uma régua</div>',
        '</div>',
    ].join('');
    const sample = [
        '<div class="label">',
        `<div class="line">${escapeHtml(`${String(sheet.widthMm)} × ${String(sheet.heightMm)} mm`)}</div>`,
        renderBlock({ kind: 'barcode', symbology: 'code128', data: 'AERIS-TESTE' }, sheet),
        ruler,
        '</div>',
    ].join('');
    const css = `${styles(sheet)}
  .ruler { display: flex; flex-direction: column; align-items: center; gap: 0.4mm }
  .ruler-bar {
    width: 10mm;
    height: 1.2mm;
    background: #000;
    /* As marcas nas pontas: sem elas não se sabe onde a medida começa. */
    border-left: 0.3mm solid #fff;
    border-right: 0.3mm solid #fff;
    outline: 0.3mm solid #000;
  }
  .ruler-text { font-size: ${mm(sheet.heightMm * 0.07)} }
  `;
    return document_(BLANK_PRINT_TITLE, css, `<div class="row">${sample}</div>`);
};
/**
 * O recibo, o orçamento e o comprovante de troca, num documento próprio.
 *
 * ## Por que saiu de `app.css`
 *
 * Imprimia-se a página inteira com tudo escondido menos `#aeris-receipt`. O
 * recibo, porém, mora dentro de um diálogo centralizado na tela: o papel saía
 * com o espaço do topo da tela em branco e cortado onde o diálogo rola. E a
 * fonte de 10 pt não cabe: 48 colunas nela medem 100 mm, e a bobina de 80 mm
 * imprime 72.
 *
 * ## O tamanho
 *
 * A página tem a largura da bobina e a ALTURA DO TEXTO, calculada pelas linhas.
 * Uma página da altura do driver (ou de A4) cortaria o recibo em folhas, e é o
 * que o balcão viu. A fonte sai da largura útil: Courier New avança 0,6 em por
 * caractere, então `largura / colunas / 0,6` é a maior que cabe — com folga de
 * 5% para a fonte que o Windows escolher no lugar.
 */
const RECEIPT_ROLL_MM = {
    32: { paper: 58, printable: 48 },
    48: { paper: 80, printable: 72 },
};
const RECEIPT_LINE_HEIGHT = 1.2;
const RECEIPT_PADDING_MM = { top: 2, bottom: 8 };
/**
 * A fonte da bobina, e por que em negrito (F140).
 *
 * O orçamento saía cinza e ilegível na térmica: Courier New de ~6,7 pt, sem
 * negrito, tem o traço mais fino que um ponto da cabeça de 203 dpi, e o driver
 * do Windows desenha o que não chega a um ponto como meio-tom — chuvisco. O
 * negrito engrossa o traço para dois pontos, e Consolas (que todo Windows tem)
 * é mais cheia que a Courier. Todas avançam no máximo 0,6 em por caractere, e é
 * por essa conta que o tamanho sai: a que o Windows escolher no lugar cabe.
 */
const RECEIPT_FONT_FAMILY = 'Consolas, "Lucida Console", "Courier New", Courier, monospace';
/** As letras que o editor oferece (F161), cada uma com as que a substituem. */
const RECEIPT_FAMILIES = {
    consolas: RECEIPT_FONT_FAMILY,
    courier: '"Courier New", Courier, monospace',
    arial: 'Arial, Helvetica, sans-serif',
};
/** A linha esticada para cima, sem mudar a largura (F161). */
const scaledCss = (scale, lineMm) => scale === 1
    ? ''
    : `
  .line { transform: scaleY(${String(scale)}); transform-origin: top left; margin-bottom: ${mm(lineMm * (scale - 1))} }
  .line.big { margin-bottom: ${mm(lineMm * 2 * (scale - 1))} }`;
export const receiptFontMm = (paper) => (RECEIPT_ROLL_MM[paper].printable / paper / 0.6) * 0.98;
const receiptCss = (paper, heightMm) => {
    const roll = RECEIPT_ROLL_MM[paper];
    const font = receiptFontMm(paper);
    return `
  @page { size: ${mm(roll.paper)} ${mm(Math.ceil(heightMm))}; margin: 0 }
  html, body { margin: 0; padding: 0; background: #fff }
  .receipt {
    box-sizing: border-box;
    width: ${mm(roll.paper)};
    margin: 0;
    padding: ${mm(RECEIPT_PADDING_MM.top)} ${mm((roll.paper - roll.printable) / 2)} ${mm(RECEIPT_PADDING_MM.bottom)};
    font-family: ${RECEIPT_FONT_FAMILY};
    font-size: ${mm(font)};
    line-height: ${String(RECEIPT_LINE_HEIGHT)};
    font-weight: 700;
    color: #000;
    /* F211: a Consolas é fina mesmo em negrito; o contorno engrossa o traço. */
    -webkit-text-stroke: 0.25px #000;
    white-space: pre;
    overflow: hidden;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  `;
};
export const renderReceiptHtml = (text, paper) => {
    const font = receiptFontMm(paper);
    const lines = text.split('\n').length;
    const height = lines * font * RECEIPT_LINE_HEIGHT +
        RECEIPT_PADDING_MM.top +
        RECEIPT_PADDING_MM.bottom;
    return document_('Recibo', receiptCss(paper, height), `<pre class="receipt">${escapeHtml(text)}</pre>`);
};
/**
 * Quatro pontos por módulo numa cabeça de 8 pontos/mm (27/09).
 *
 * Eram três (0,375 mm). Impresso pelo navegador com "Ajustar à página", a
 * barra fina virava 2,7 pontos e a impressora arredondava umas para 2 e outras
 * para 3 — um cupom que não lia. Com quatro, uma redução de 10% ainda deixa
 * as barras em proporção.
 */
const RECEIPT_MODULE_MM = 0.5;
const RECEIPT_BARS_MM = 12;
/** Zona de silêncio: dez módulos brancos de cada lado, como pede o CODE128. */
const RECEIPT_QUIET_MM = 10 * RECEIPT_MODULE_MM;
/**
 * O documento na bobina COM o que o texto puro perde (F140).
 *
 * `renderText` achata tudo em linhas de caracteres, e o recibo, o orçamento e
 * o comprovante de troca eram impressos a partir disso: o nome da loja, o
 * "ORÇAMENTO", o número e o TOTAL perdiam o negrito e o tamanho grande, e o
 * código de barras do pé virava só os dígitos — que o leitor do caixa não lê.
 *
 * As linhas continuam vindo de `layout`, o mesmo motor do ESC/POS: as colunas
 * alinham igual e o que um teste afirma sobre o texto vale no papel. O que
 * muda é o desenho de cada linha — grande em dobro (a linha já foi quebrada na
 * metade da largura) e o código de barras em barras de verdade.
 */
export const renderReceiptDocumentHtml = (document, paper) => {
    const roll = RECEIPT_ROLL_MM[paper];
    const lineMm = receiptFontMm(paper) * RECEIPT_LINE_HEIGHT;
    /*
     * A letra da loja (F161). O tamanho estica cada linha para CIMA: a largura
     * não muda, então as colunas continuam as de `layout` e nenhum valor é
     * cortado à direita. `transform` não ocupa espaço, e a margem de baixo
     * devolve à linha a altura que ela ganhou.
     */
    const scale = PRINT_FONT_SCALE[document.style?.size ?? 'normal'];
    const family = RECEIPT_FAMILIES[document.style?.family ?? 'consolas'];
    /*
     * Sempre 700 — F211. O "Negrito" desmarcado no modelo deixava a nota em 400,
     * e na térmica de 203 dpi o traço de 400 some: "deixar as cores da nota mais
     * fortes pra enxergarem direito". A opção continua no tipo, para o modelo
     * gravado ler, mas não afina mais nada.
     */
    const weight = 700;
    /*
     * Uma página por via — 22/10. O corte no MEIO (as duas vias, F150) não
     * existe no navegador: as vias saíam grudadas num papel só. Cada trecho
     * entre cortes vira uma página, e o driver da térmica corta no fim de cada
     * página. Todas com a altura da maior: a `@page` é uma só.
     */
    const padding = RECEIPT_PADDING_MM.top + RECEIPT_PADDING_MM.bottom;
    const pages = [];
    let height = padding;
    let body = [];
    for (const line of layout(document, paper)) {
        const { block } = line;
        if (block.kind === 'cut') {
            pages.push({ body, height });
            body = [];
            height = padding;
            continue;
        }
        if (block.kind === 'barcode') {
            let pattern = null;
            try {
                pattern =
                    block.symbology === 'ean13'
                        ? encodeEan13(block.data)
                        : encodeCode128(block.data);
            }
            catch {
                // Dado que não vira símbolo sai só como texto: melhor o número legível
                // do que um papel que não sai.
            }
            if (pattern !== null) {
                const widthMm = Math.min(roll.printable - 2 * RECEIPT_QUIET_MM, pattern.totalModules * RECEIPT_MODULE_MM);
                body.push(`<div class="bars-box" style="width:${mm(widthMm)}">${barcodeSvg(pattern, RECEIPT_BARS_MM)}</div>`);
                height += RECEIPT_BARS_MM + 1;
            }
            body.push(`<div class="line">${escapeHtml(line.text)}</div>`);
            height += lineMm * scale;
            continue;
        }
        /*
         * Rótulo e valor como DOIS elementos, e não um texto com espaços — F160.
         *
         * O papel impresso na loja saía com os valores uma linha acima dos rótulos:
         * a data ao lado do "Nº", o cliente ao lado de "Data", o "100,00" acima de
         * "Subtotal". A prévia na tela estava certa, e o HTML também — rótulo e
         * valor eram UM texto, na mesma `div`. Quem deslocava era o driver da
         * térmica, que troca a fonte do navegador pela dele e reposiciona o pedaço
         * de texto depois de uma sequência longa de espaços.
         *
         * Com dois `span` numa linha flexível, a posição do valor não depende de
         * quantos espaços cabem na fonte que o driver escolheu. O corte continua
         * sendo o de `pairParts`, o mesmo motor da prévia e do ESC/POS.
         */
        if (block.kind === 'pair') {
            const parts = pairParts(block.label, block.value, paper);
            body.push(`<div class="line pair"><span>${escapeHtml(parts.label)}</span><span>${escapeHtml(parts.value)}</span></div>`);
            height += lineMm * scale;
            continue;
        }
        const big = block.kind === 'text' && block.big === true;
        body.push(`<div class="${big ? 'line big' : 'line'}">${escapeHtml(line.text)}</div>`);
        height += (big ? lineMm * 2 : lineMm) * scale;
    }
    pages.push({ body, height });
    const tallest = Math.max(...pages.map((each) => each.height));
    const css = `${receiptCss(paper, tallest)}
  .receipt { white-space: normal; font-family: ${family}; font-weight: ${String(weight)} }
  .receipt + .receipt { break-before: page; page-break-before: always }
  .line { height: ${mm(lineMm)}; white-space: pre; overflow: hidden }
  .big { font-size: ${mm(receiptFontMm(paper) * 2)}; height: ${mm(lineMm * 2)} }${scaledCss(scale, lineMm)}
  .pair { display: flex; justify-content: space-between; gap: 1ch }
  .pair > span:first-child { overflow: hidden }
  .pair > span:last-child { flex: none }
  .bars-box { margin: 1mm auto 0 }
  .bars { display: block; width: 100%; fill: #000 }
  `;
    return document_(document.title, css, pages
        .map((each) => `<div class="receipt">${each.body.join('')}</div>`)
        .join(''));
};
//# sourceMappingURL=html.js.map